import React, { useState, useEffect } from 'react'
import { Modal, Box, IconButton, Tabs, Tab, Button } from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import EdTextfield from '@/components/forms/EdTextfield'
import EdSwitch from '@/components/forms/EdSwitch'
import EdSelect from '@/components/forms/EdSelect'

interface AddTestModalProps {
  isOpen: boolean
  onClose: () => void
  initialTab?: number
  mode?: 'create' | 'edit'
  testData?: any
}

interface Answer {
  id: number
  text: string
  score: number
  isCorrect: boolean
}

interface Question {
  id: number
  type: string
  question: string
  description: string
  answers: Answer[]
}

const AddTestModal: React.FC<AddTestModalProps> = ({ isOpen, onClose, initialTab = 0, mode = 'create', testData = null }) => {
  const [activeTab, setActiveTab] = useState(initialTab)
  const [useQuestionsFromMaterial, setUseQuestionsFromMaterial] = useState(false)
  const [published, setPublished] = useState(false)
  const [selectedPrerequisites, setSelectedPrerequisites] = useState<boolean[]>(Array(12).fill(false))

  // Update active tab when initialTab prop changes
  useEffect(() => {
    setActiveTab(initialTab)
  }, [initialTab])

  // Populate form data when in edit mode
  useEffect(() => {
    if (mode === 'edit' && testData) {
      // Qui potresti popolare i campi del form con i dati del test
      // setUseQuestionsFromMaterial(testData.useQuestionsFromMaterial)
      // setPublished(testData.published)
      // etc.
    }
  }, [mode, testData])
  
  // Questions state
  const [questions, setQuestions] = useState<Question[]>([
    {
      id: 1,
      type: 'multiple-choice',
      question: 'CHE COS\'E\' IL PERICOLO?',
      description: '',
      answers: [
        { id: 1, text: '', score: 3, isCorrect: false },
        { id: 2, text: '', score: 3, isCorrect: false }
      ]
    }
  ])
  const [showNewQuestionForm, setShowNewQuestionForm] = useState(false)
  const [newQuestion, setNewQuestion] = useState({
    type: 'multiple-choice',
    question: '',
    description: '',
    answers: [
      { id: 1, text: '', score: 3, isCorrect: false },
      { id: 2, text: '', score: 3, isCorrect: false }
    ]
  })

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleSave = () => {
    // TODO: Implement save logic
    console.log('Saving test data...')
    onClose()
  }

  const handleCancel = () => {
    onClose()
  }

  const handlePrerequisiteToggle = (index: number) => {
    setSelectedPrerequisites(prev => {
      const newState = [...prev]
      newState[index] = !newState[index]
      return newState
    })
  }

  const handleAddNewQuestion = () => {
    setShowNewQuestionForm(true)
  }

  const handleSaveNewQuestion = () => {
    const questionToAdd = {
      ...newQuestion,
      id: questions.length + 1
    }
    setQuestions([...questions, questionToAdd])
    setShowNewQuestionForm(false)
    setNewQuestion({
      type: 'multiple-choice',
      question: '',
      description: '',
      answers: [
        { id: 1, text: '', score: 3, isCorrect: false },
        { id: 2, text: '', score: 3, isCorrect: false }
      ]
    })
  }

  const handleCancelNewQuestion = () => {
    setShowNewQuestionForm(false)
    setNewQuestion({
      type: 'multiple-choice',
      question: '',
      description: '',
      answers: [
        { id: 1, text: '', score: 3, isCorrect: false },
        { id: 2, text: '', score: 3, isCorrect: false }
      ]
    })
  }

  const handleAddNewAnswer = () => {
    const newAnswerId = newQuestion.answers.length + 1
    setNewQuestion(prev => ({
      ...prev,
      answers: [...prev.answers, { id: newAnswerId, text: '', score: 3, isCorrect: false }]
    }))
  }

  const handleRemoveAnswer = (answerId: number) => {
    setNewQuestion(prev => ({
      ...prev,
      answers: prev.answers.filter(answer => answer.id !== answerId)
    }))
  }

  const handleAnswerChange = (answerId: number, field: keyof Answer, value: any) => {
    setNewQuestion(prev => ({
      ...prev,
      answers: prev.answers.map(answer => 
        answer.id === answerId ? { ...answer, [field]: value } : answer
      )
    }))
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="add-test-modal-title"
      aria-describedby="add-test-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '900px' },
          height: { xs: '90vh', sm: '80vh' },
          bgcolor: 'background.paper',
          borderRadius: 0,
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
       

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, pt: 2, backgroundColor: '#F8F9F9' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="test tabs"
            variant="fullWidth"
            sx={{
                outline: 'none',
                
                border: 'none',
                '& .MuiTabs-indicator': {
                  backgroundColor: '#B80F15',
                  height: 3,
                },
                '& .MuiTab-root': {
                  color: '#6B7280',
                  fontWeight: 500,
                  padding: '10px 0px',
                  textTransform: 'uppercase',
                  fontSize: '0.875rem',
                  minHeight: 48,
                  '&.Mui-selected': {
                    color: '#B80F15',
                    fontWeight: 600,
                    outline: 'none',
                    boxShadow: 'none',
                    border: 'none',
                  },
                  '&:hover': {
                    color: '#B80F15',
                    backgroundColor: 'rgba(184, 15, 21, 0.04)',
                  },
                  '&:active':{
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                  },
                  '&:focus':{
                    border: 'none',
                    boxShadow: 'none',
                    outline: 'none',
                  }
                },
              }}
          >
            <Tab label="Dati test finale" />
            <Tab label={`Domande del test (${questions.length})`} />
            <Tab label={`Prerequisiti (12)`} />
          </Tabs>
        </Box>
        
        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {activeTab === 0 && (
           <div className="flex flex-col gap-4 px-8 pb-6">  
            <h2 className="text-xl text-[#4F4F4F] font-bold mb-2">
              {mode === 'edit' ? 'Modifica test finale' : 'Dati test finale'}
            </h2>
            <div className="flex flex-row gap-4 w-full ">
                <div className="w-full sm:w-1/3">
                <EdTextfield label="Nome corso" id={'name'} type={'text'} placeholder={'Nome corso'} />
                </div>
                <div className="w-full sm:w-1/3">
                <EdTextfield label="Nome questionario" id={'questionnaireName'} type={'text'} placeholder={'Nome questionario'} />
                </div>
                <div className="w-full sm:w-1/3">
                <EdTextfield label="Descrizione" id={'description'} type={'text'} placeholder={'Descrizione'} />
                </div>
            </div>
             <div className="flex flex-row gap-4 w-full ">
                 <div className="w-full sm:w-1/3">
                 <EdTextfield label="Punteggio minimo" id={'minScore'} type={'number'} placeholder={'Punteggio minimo'} />
                 </div>
                 <div className="w-full sm:w-1/3">
                 <EdTextfield label="Numero tentativi" id={'numberOfAttempts'} type={'number'} placeholder={'Numero tentativi'} />
                 </div>
                 <div className="w-full sm:w-1/3">
                 <EdTextfield label="Numero domande da assegnare" id={'questionsNumber'} type={'number'} placeholder={'Numero domande da assegnare'} />
                 </div>
             </div>
             
             {/* Switch Row */}
             <div className="flex flex-row gap-8 w-full mt-4">
                 <div className="w-full">
                     <EdSwitch 
                         id="useQuestionsFromMaterial"
                         label="Usa le domande allegata al materiale didattico (vanno abilitati da tab prerequisiti)"
                         value={useQuestionsFromMaterial}
                         onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUseQuestionsFromMaterial(e.target.checked)}
                     />
                 </div>
                 <div className="flex-1">
                     <EdSwitch 
                         id="published"
                         label="Pubblicato"
                         value={published}
                         onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPublished(e.target.checked)}
                     />
                 </div>
             </div>
            </div>
          )}
          {activeTab === 1 && (
            <div className="flex flex-col px-8 pb-6">
              <h2 className="text-xl text-[#4F4F4F] font-bold mb-4">Domande del test</h2>
              
              {/* Existing Questions List */}
              <div className="space-y-2 mb-2">
                {questions.map((question, index) => (
                  <motion.div 
                    key={question.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="bg-white"
                  >
                    {/* Question Header */}
                    <div className="flex items-center justify-between mb-0">
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-[#4F4F4F]">Dom. a risposta multipla</span>
                        <span className="text-sm font-medium text-[#4F4F4F]">{question.question}</span>
                        <span className="text-sm text-[#4F4F4F]">3,6 punti</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <IconButton size="small">
                        <i className="ri-pencil-fill"></i>
                        </IconButton>
                        <IconButton size="small">
                        <i className="ri-more-2-fill"></i>                       
                         </IconButton>
                       
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {/* Add New Question Button */}
              <div className="mb-4">
                <span 
                  className="text-[#B80F15] text-sm font-medium hover:text-red-700 transition-colors cursor-pointer"
                  onClick={handleAddNewQuestion}
                >
                  + Aggiungi nuova domanda
                </span>
              </div>
              
              {/* New Question Form */}
              <AnimatePresence>
                {showNewQuestionForm && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="bg-white"
                  >
                  {/* Question Header */}
              
                  
                  {/* Question Details */}
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-1/3">
                      <EdSelect
                        id="new-question-type"
                        label="Tipologia domanda"
                        value={newQuestion.type}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNewQuestion(prev => ({ ...prev, type: e.target.value }))}
                        options={[
                          { value: 'multiple-choice', label: 'Risposta multipla' },
                          { value: 'open-question', label: 'Domanda aperta' }
                        ]}
                      />
                    </div>
                    <div className="w-1/3">
                      <EdTextfield 
                        id="new-question-text"
                        label="Domanda"
                        type="text"
                        placeholder="Domanda"
                        register={{
                          onChange: (e: React.ChangeEvent<HTMLInputElement>) => setNewQuestion(prev => ({ ...prev, question: e.target.value }))
                        }}
                      />
                    </div>
                    <div className="w-1/3">
                      <EdTextfield 
                        id="new-question-desc"
                        label="Descrizione"
                        type="text"
                        placeholder="Descrizione"
                        register={{
                          onChange: (e: React.ChangeEvent<HTMLInputElement>) => setNewQuestion(prev => ({ ...prev, description: e.target.value }))
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Answers Structure */}
                  <AnimatePresence>
                    {newQuestion.type === 'multiple-choice' && (
                      <motion.div 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2, delay: 0.1 }}
                        className="ml-4 border-l-2 border-gray-300 pl-4"
                      >
                      {newQuestion.answers.map((answer, index) => (
                        <motion.div 
                          key={answer.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2, delay: index * 0.1 }}
                          className="mb-4 last:mb-0 relative "
                        >
                          {/* Horizontal line connecting to vertical line */}
                          <div className="absolute left-0 top-1/2 w-4 h-px bg-gray-300 transform -translate-y-1/2"></div>
                          <div className="flex items-center gap-4 ml-6">
                            <div className="flex-1">
                              <EdTextfield 
                                id={`new-answer-${answer.id}`}
                                label={`Risposta n°${index + 1}*`}
                                type="text"
                                placeholder={`Risposta n°${index + 1}*`}
                                register={{
                                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleAnswerChange(answer.id, 'text', e.target.value)
                                }}
                              />
                            </div>
                            <div className="w-24">
                              <EdTextfield 
                                id={`new-score-${answer.id}`}
                                label="Punteggio"
                                type="number"
                                placeholder="Punteggio"
                                register={{
                                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleAnswerChange(answer.id, 'score', parseInt(e.target.value) || 0)
                                }}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <EdSwitch 
                                id={`new-correct-${answer.id}`}
                                label="Corretta"
                                value={answer.isCorrect}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAnswerChange(answer.id, 'isCorrect', e.target.checked)}
                              />
                            </div>
                            {index > 1 && (
                              <IconButton size="small" className="text-red-500" onClick={() => handleRemoveAnswer(answer.id)}>
                               <i className="ri-delete-bin-6-fill"></i>
                              </IconButton>
                            )}
                          </div>
                        </motion.div>
                      ))}
                      
                      {/* Add New Answer Button */}
                      <div className="mt-2">
                        <span 
                          className="text-[#B80F15] text-sm font-medium hover:text-red-700 transition-colors cursor-pointer"
                          onClick={handleAddNewAnswer}
                        >
                          + Aggiungi nuova risposta
                        </span>
                      </div>
                      </motion.div>
                    )}
                    
                    {newQuestion.type === 'open-question' && (
                      <motion.div 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2, delay: 0.1 }}
                        className="ml-0 pl-0"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-1/3">
                            <EdTextfield 
                              id="open-question-score"
                              label="Punteggio"
                              type="number"
                              placeholder="Punteggio"
                            />
                          </div>
                          <div className="w-1/3">
                            <EdTextfield 
                              id="open-question-answer"
                              label="Risposta"
                              type="text"
                              placeholder="Risposta"
                            />
                          </div>
                          <div className="w-1/3">
                        </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  {/* Form Action Buttons */}
                  <div className="flex items-center gap-2 mt-4">
                    <Button
                      variant="contained"
                      onClick={handleSaveNewQuestion}
                      sx={{
                        backgroundColor: '#B80F15',
                        textTransform: 'uppercase',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        px: 3,
                        py: 1,
                        '&:hover': {
                          backgroundColor: '#A00D13',
                        },
                      }}
                    >
                      AGGIUNGI
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCancelNewQuestion}
                      sx={{
                        backgroundColor: '#E3EBF7',
                        color: '#285192',
                        textTransform: 'uppercase',
                        fontSize: '12px',
                        fontWeight: 'medium',
                        px: 3,
                        py: 1,
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#BDBDBD',
                        },
                      }}
                    >
                      ANNULLA
                    </Button>
                  </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
          {activeTab === 2 && (
            <div className="flex flex-col px-8 pb-6">
              <h2 className="text-xl text-[#4F4F4F] font-bold mb-4">Materiale propedeutico</h2>
              
              {/* Prerequisites List */}
              <div className="space-y-0">
                {Array.from({ length: 12 }, (_, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    className="flex items-center justify-between py-4 border-b border-gray-200 last:border-b-0"
                  >
                    {/* File Name */}
                    <div className="flex-1">
                      <span className="text-[#4F4F4F] text-sm">
                        CONCETTI CHIAVE D.LGS. 81.2008 - I.mp4
                      </span>
                    </div>
                    
                    {/* Test Questions Count */}
                    <div className="flex-1 text-center">
                      <span className="text-[#4F4F4F] text-sm">
                        Domande test : <span className="text-blue-400">5</span>
                      </span>
                    </div>
                    
                    {/* Toggle Switch */}
                    <div className="flex items-center gap-2">
                      <EdSwitch 
                        id={`prerequisite-${index}`}
                        label=""
                        value={selectedPrerequisites[index]}
                        onChange={() => handlePrerequisiteToggle(index)}
                      />
                      <span className={`text-sm ${selectedPrerequisites[index] ? 'text-[#4F4F4F]' : 'text-gray-400'}`}>
                        Selezionato
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
              <h2 className="text-xl text-[#4F4F4F] font-bold mb-4">Test propedeutico</h2>
              <motion.div 
                    key={0}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: 0.2 * 0.05 }}
                    className="flex items-center justify-between py-4 border-b border-gray-200 last:border-b-0"
                  >
                    {/* File Name */}
                    <div className="flex-1">
                      <span className="text-[#4F4F4F] text-sm">
                        CONCETTI CHIAVE D.LGS. 81.2008 - I.mp4
                      </span>
                    </div>
                    
                    {/* Test Questions Count */}
                    <div className="flex-1 text-center">
                      <span className="text-[#4F4F4F] text-sm">
                        Domande test : <span className="text-blue-400">5</span>
                      </span>
                    </div>
                    
                    {/* Toggle Switch */}
                    <div className="flex items-center gap-2">
                      <EdSwitch 
                        id={`prerequisite`}
                        label=""
                        value={true}
                        onChange={() => {}}
                      />
                      <span className={`text-sm text-[#4F4F4F]`}>
                        Selezionato
                      </span>
                    </div>
                  </motion.div>
            </div>
          )}
        </Box>

        {/* Footer with buttons */}
        <Box sx={{ 
          borderTop: 1, 
          borderColor: 'divider', 
          px: 4,
          py: 2,
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 2
        }}>
         
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              backgroundColor: '#B80F15',
              px: 4,
              '&:hover': {
                backgroundColor: '#A00D13',
              },
            }}
          >
            {mode === 'edit' ? 'Aggiorna' : 'Salva'}
          </Button>
          <Button
            variant="contained"
            onClick={handleCancel}
            sx={{
              backgroundColor: '#E3EBF7', // bg-primary-100 equivalent
              color: '#285192', // text-primary-800 equivalent
              textTransform: 'uppercase',
              fontSize: '12px',
              fontWeight: 'medium',
              px: 3,
              py: 1.5,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#BDBDBD', // hover:bg-gray-300 equivalent
              },
            }}
          >
            Annulla
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default AddTestModal
